import { useCallback, useEffect, useState } from 'react';
import { NotebookPen, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import type { EventDetail } from '../api/events';
import { saveProgram } from '../api/eventProgram';
import { dutiesApi } from '../api/eventDuties';
import type { EventDuties, EventDuty } from '../api/eventDuties';
import { rosterApi } from '../api/auth';
import type { RosterRow } from '../api/auth';
import { subgroupsApi } from '../api/subgroups';
import type { Subgroup } from '../api/subgroups';
import { formatInZone, formatTimeInZone } from '../lib/datetime';
import { Button } from '../ui/Button';
import { ManageBlock } from '../ui/DocketRow';
import { EmptyState } from '../ui/EmptyState';
import { TextAreaField, TextField } from '../ui/Field';
import { PanelTabs } from '../ui/Tabs';

/**
 * The program panel — TROOP-159, TROOP-162, TROOP-163, TROOP-164, TROOP-165.
 *
 * What is actually happening at this event: a theme, the running order under it, and who is down
 * for each part.
 *
 * **This is the youth's section.** Almost every rule in this product that reads position and
 * classification together lands on adults only — a payment, a returned medical form, a moderation
 * report — and between them they have made "officer" read like a synonym for "adult officer". The
 * program inverts that: the Senior Patrol Leader plans the meeting, so `canWrite` is position alone
 * and a fifteen-year-old Patrol Leader is who it is usually true for. The server decides it;
 * nothing here re-derives it.
 *
 * **The theme and the plan sit above the tabs, and the duties inside them.** `PanelTabs` means
 * *what this reader owes* against *the unit* — and the running order is neither, it is what the
 * evening is. Putting it in a tab would make a reader choose between the plan and their own part
 * in it, which is precisely the choice nobody wants to make.
 *
 * **Plain text throughout, with `white-space: pre-wrap` and nothing else.** No markdown, no
 * linkifying, no `dangerouslySetInnerHTML`. A running order is mostly newlines, so preserving them
 * is the whole rendering requirement — and it is a CSS property rather than a parser. A pasted URL
 * is literal characters: only curated text (`CuratedText`, an announcement's body) draws links.
 */
export function EventProgram({
  unitId,
  event,
  canWrite,
  onSaved,
}: {
  /**
   * Undefined on `/family`. A guardian reads the program and arranges none of it — rule 13a's
   * boundary: what a child may do about their own things, not an office the child holds. They do
   * tick, which is a different question and has its own route.
   */
  unitId?: string;
  event: EventDetail;
  canWrite: boolean;
  /** Re-reads the event, so the subtitle above and the docket row follow a save. */
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [theme, setTheme] = useState(event.theme ?? '');
  const [plan, setPlan] = useState(event.programPlan ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duties, setDuties] = useState<EventDuties | null>(null);
  // Opens on "Yours", which is the reader's own part in the evening. Not persisted: a duty they
  // have not ticked should still be asking on the next visit.
  const [tab, setTab] = useState('yours');

  const reloadDuties = useCallback(() => {
    dutiesApi
      .list(unitId, event.id)
      .then(setDuties)
      .catch(() => undefined);
  }, [unitId, event.id]);

  useEffect(reloadDuties, [reloadDuties]);

  const written = Boolean(event.theme || event.programPlan);

  const open = () => {
    // Seeded from what is on screen each time it opens, so a cancelled edit leaves nothing behind
    // and a save by somebody else since the page loaded is not silently typed over.
    setTheme(event.theme ?? '');
    setPlan(event.programPlan ?? '');
    setError(null);
    setEditing(true);
  };

  const submit = async () => {
    if (!unitId) return;

    setBusy(true);
    setError(null);

    try {
      // Sent raw. The domain trims on the way in, which is the one place it belongs -- trimming in
      // an onChange is what makes the space bar appear dead on a controlled input (TROOP-126).
      await saveProgram(unitId, event.id, { theme, plan });
      setEditing(false);
      onSaved();
    } catch {
      setError('Could not save the program. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const mine = duties?.duties.filter((d) => d.isYours) ?? [];

  return (
    <div>
      {written ? (
        <div>
          {event.theme && (
            <p
              className="t-body-strong"
              style={{ color: 'var(--color-text-primary)', margin: '0 0 var(--space-3)' }}
            >
              {event.theme}
            </p>
          )}

          {event.programPlan && (
            <p
              className="t-body"
              style={{
                color: 'var(--color-text-primary)',
                margin: 0,
                // The plan IS its line breaks. No parser, no markdown -- see the header.
                whiteSpace: 'pre-wrap',
              }}
            >
              {event.programPlan}
            </p>
          )}

          {/* Who wrote it and when. The useful question about a plan is almost always "is this the
              current one" -- a running order typed three weeks ago for a different meeting reads
              exactly like tonight's. */}
          {event.programUpdatedAt && event.programUpdatedBy && (
            <p
              className="t-caption"
              style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-4) 0 0' }}
            >
              {event.programUpdatedBy} &middot;{' '}
              {formatInZone(event.programUpdatedAt, event.timeZoneId)}
            </p>
          )}
        </div>
      ) : (
        /* The audience genuinely differs: for an officer this is the thing to do next, for
           everybody else it is a fact about the event. No disabled control either way -- there is
           nothing for a member to act on, so the sentence names who does. */
        <EmptyState
          icon={NotebookPen}
          headline="Nothing planned yet"
          explanation={
            canWrite
              ? 'Say what this one is about, and what happens when.'
              : 'Whoever is running this will fill it in.'
          }
        />
      )}

      {duties && (
        <div style={{ marginTop: 'var(--space-5)' }}>
          <PanelTabs
            label="Who is doing what"
            selected={tab}
            onSelect={setTab}
            yours={
              <DutyList
                duties={mine}
                zone={event.timeZoneId}
                emptyLine="Nothing on the program is yours tonight."
                unitId={unitId}
                eventId={event.id}
                onChanged={reloadDuties}
              />
            }
            everyone={
              <>
                <DutyList
                  duties={duties.duties}
                  zone={event.timeZoneId}
                  emptyLine="Nobody is down for anything yet."
                  unitId={unitId}
                  eventId={event.id}
                  onChanged={reloadDuties}
                />

                {/* Configuration is activated, never resting -- the same gesture every panel uses. */}
                {duties.canWrite && unitId && (
                  <DutyManager
                    unitId={unitId}
                    eventId={event.id}
                    duties={duties}
                    onChanged={reloadDuties}
                  />
                )}
              </>
            }
          />
        </div>
      )}

      {canWrite && unitId && (
        <ManageBlock
          label={written ? 'Change the program' : 'Write the program'}
          open={editing}
          onToggle={() => (editing ? setEditing(false) : open())}
        >
          <TextField
            label="Theme"
            hint="One line: what this one is about. It shows under the event's name on the calendar."
            value={theme}
            maxLength={200}
            placeholder="Knots and lashings"
            onChange={(e) => setTheme(e.target.value)}
          />

          <TextAreaField
            label="The plan"
            hint="What happens, and roughly when. Line breaks are kept."
            rows={10}
            value={plan}
            maxLength={8000}
            placeholder={'7:00  Opening, flags\n7:15  Knots\n8:15  Game\n8:45  Closing'}
            onChange={(e) => setPlan(e.target.value)}
          />

          {error && (
            <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)' }}>
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center" style={{ gap: 'var(--space-3)' }}>
            <Button onClick={() => void submit()} disabledReason={busy ? 'Saving…' : undefined}>
              Save the program
            </Button>

            <Button variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </ManageBlock>
      )}
    </div>
  );
}

/**
 * The running order, or one reader's part of it.
 *
 * **A checkbox, not an icon.** A control that records something must not be drawn as the state it
 * records (TROOP-135) — checked is done, unchecked is not, and the same control undoes it.
 *
 * **Where a reader may not tick, no control renders and the row says who does.** A greyed box owes
 * an explanation, and the honest sentence here is about whose job it is rather than about the
 * reader's permission — which matters because the answer changes when a Scout is elected in the
 * spring and their parent quietly stops being able to tick.
 */
function DutyList({
  duties,
  zone,
  emptyLine,
  unitId,
  eventId,
  onChanged,
}: {
  duties: EventDuty[];
  zone: string;
  emptyLine: string;
  unitId?: string;
  eventId: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  if (duties.length === 0) {
    return (
      <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        {emptyLine}
      </p>
    );
  }

  const toggle = async (duty: EventDuty) => {
    setBusy(duty.id);
    try {
      await dutiesApi.setDone(unitId, eventId, duty.id, !duty.isDone);
      onChanged();
    } catch {
      // The list reloads either way; a failed tick simply stays unticked rather than lying.
      onChanged();
    } finally {
      setBusy(null);
    }
  };

  return (
    <ul className="flex flex-col" style={{ gap: 'var(--space-2)', margin: 0, padding: 0 }}>
      {duties.map((duty) => (
        <li
          key={duty.id}
          className="flex items-start"
          style={{ gap: 'var(--space-3)', listStyle: 'none' }}
        >
          {duty.canCheck ? (
            <input
              type="checkbox"
              checked={duty.isDone}
              disabled={busy === duty.id}
              onChange={() => void toggle(duty)}
              aria-label={`${duty.label} is done`}
              style={{ width: 18, height: 18, marginTop: '0.2em', flexShrink: 0 }}
            />
          ) : (
            // A spacer rather than a disabled box: the row below names who acts.
            <span aria-hidden="true" style={{ width: 18, flexShrink: 0 }} />
          )}

          <span className="flex flex-col min-w-0" style={{ gap: '2px' }}>
            <span
              className="t-body"
              style={{
                color: 'var(--color-text-primary)',
                textDecoration: duty.isDone ? 'line-through' : undefined,
              }}
            >
              {/* The time first where there is one, so the column reads as a running order. */}
              {duty.startsAt && (
                <span className="tabular" style={{ color: 'var(--color-text-secondary)' }}>
                  {formatTimeInZone(duty.startsAt, zone)}
                  {duty.durationMinutes ? ` · ${duty.durationMinutes} min` : ''}
                  {'  '}
                </span>
              )}
              {duty.label}
            </span>

            <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
              {assigneeLine(duty)}
              {duty.isDone && duty.completedByName ? ` · done, ${duty.completedByName}` : ''}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Who is down for it, and — where this reader cannot tick — who does.
 *
 * The second half is the disclosure TROOP-164 asks for: a Scout elected Patrol Leader in the spring
 * silently takes the tick away from their parent, and a row that just loses its checkbox with
 * nothing said is the silent failure this repo keeps getting bitten by.
 */
function assigneeLine(duty: EventDuty): string {
  if (duty.assignedToSubgroupName) {
    return duty.canCheck ? duty.assignedToSubgroupName : `${duty.assignedToSubgroupName} tick this one`;
  }

  if (duty.assignedToName) {
    return duty.canCheck ? duty.assignedToName : `${duty.assignedToName} ticks this one`;
  }

  return 'Nobody yet';
}

/**
 * The officer's side: add a job, say who has it, put it in the running order, remove it.
 *
 * **Assignment is one `<select>` holding people and groups together**, which is what a leader
 * actually thinks — "give this to Ryan, or give it to Hawk Patrol" is one decision, not two. The
 * group options render the unit's own word for a group (rule 16) rather than a literal.
 */
function DutyManager({
  unitId,
  eventId,
  duties,
  onChanged,
}: {
  unitId: string;
  eventId: string;
  duties: EventDuties;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [people, setPeople] = useState<RosterRow[]>([]);
  const [groups, setGroups] = useState<Subgroup[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;

    // Fetched when the block opens rather than with the panel: a roster is hundreds of rows and
    // most readers of this panel never arrange anything.
    rosterApi.list(unitId).then(setPeople).catch(() => undefined);
    subgroupsApi.list(unitId).then((all) => setGroups(all.filter((g) => !g.isRetired))).catch(() => undefined);
  }, [open, unitId]);

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await work();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const add = () => {
    const text = label.trim();
    if (!text) return;

    void run(async () => {
      await dutiesApi.add(unitId, eventId, text);
      setLabel('');
    });
  };

  const move = (index: number, by: number) => {
    const order = duties.duties.map((d) => d.id);
    const to = index + by;
    if (to < 0 || to >= order.length) return;

    [order[index], order[to]] = [order[to], order[index]];
    void run(() => dutiesApi.reorder(unitId, eventId, order));
  };

  return (
    <ManageBlock label="Arrange the program" open={open} onToggle={() => setOpen(!open)}>
      <div className="flex flex-wrap items-end" style={{ gap: 'var(--space-3)' }}>
        <span style={{ flex: '1 1 12rem' }}>
          <TextField
            label="Add a job"
            value={label}
            maxLength={300}
            placeholder="Opening flags"
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
        </span>

        <Button onClick={add} disabledReason={busy ? 'Saving…' : undefined}>
          Add
        </Button>
      </div>

      {duties.duties.map((duty, index) => (
        <div
          key={duty.id}
          className="flex flex-wrap items-center"
          style={{
            gap: 'var(--space-2)',
            paddingTop: 'var(--space-3)',
            borderTop: '1px solid var(--color-border-subtle)',
          }}
        >
          <span className="t-body" style={{ flex: '1 1 8rem', color: 'var(--color-text-primary)' }}>
            {duty.label}
          </span>

          <label className="inline-flex items-center gap-1">
            <span className="sr-only-focusable t-caption">Who has {duty.label}</span>
            <select
              value={
                duty.assignedToPersonId
                  ? `p:${duty.assignedToPersonId}`
                  : duty.assignedToSubgroupId
                    ? `g:${duty.assignedToSubgroupId}`
                    : ''
              }
              disabled={busy}
              onChange={(e) => {
                const v = e.target.value;
                void run(() =>
                  dutiesApi.update(unitId, eventId, duty.id, {
                    unassign: v === '',
                    assignedToPersonId: v.startsWith('p:') ? v.slice(2) : null,
                    assignedToSubgroupId: v.startsWith('g:') ? v.slice(2) : null,
                  }),
                );
              }}
              style={selectStyle}
            >
              <option value="">Nobody yet</option>
              <optgroup label={duties.subgroupLabelPlural}>
                {groups.map((g) => (
                  <option key={g.id} value={`g:${g.id}`}>
                    {g.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="People">
                {people.map((p) => (
                  <option key={p.personId} value={`p:${p.personId}`}>
                    {p.displayName}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>

          <label className="inline-flex items-center gap-1">
            <span className="sr-only-focusable t-caption">Minutes in</span>
            <input
              type="number"
              defaultValue={duty.startOffsetMinutes ?? ''}
              disabled={busy}
              placeholder="min in"
              aria-label={`Minutes after the start for ${duty.label}`}
              onBlur={(e) => {
                const raw = e.target.value.trim();
                void run(() =>
                  dutiesApi.update(unitId, eventId, duty.id, {
                    clearSchedule: raw === '',
                    startOffsetMinutes: raw === '' ? null : Number(raw),
                    durationMinutes: duty.durationMinutes,
                  }),
                );
              }}
              style={{ ...selectStyle, width: '5.5rem' }}
            />
          </label>

          <button
            type="button"
            onClick={() => move(index, -1)}
            aria-label={`Move ${duty.label} earlier`}
            style={iconButton}
          >
            <ChevronUp size={16} aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={() => move(index, 1)}
            aria-label={`Move ${duty.label} later`}
            style={iconButton}
          >
            <ChevronDown size={16} aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={() => void run(() => dutiesApi.remove(unitId, eventId, duty.id))}
            aria-label={`Remove ${duty.label}`}
            style={iconButton}
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>
      ))}
    </ManageBlock>
  );
}

const selectStyle = {
  minHeight: 'var(--tap-min)',
  borderRadius: 'var(--radius-md)',
  border: '1.5px solid var(--color-border-strong)',
  background: 'var(--color-surface-raised)',
  color: 'var(--color-text-primary)',
  padding: '0 var(--space-2)',
} as const;

const iconButton = {
  minHeight: 'var(--tap-min)',
  minWidth: 'var(--tap-min)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  color: 'var(--color-text-muted)',
  cursor: 'pointer',
} as const;
